import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { DownloadSimple, X, AppleLogo } from "@phosphor-icons/react";

const DISMISS_KEY = "pwa_install_dismissed_at";
const DISMISS_DAYS = 7;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    const recentlyDismissed = Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
    if (recentlyDismissed) return;

    // Detect iOS Safari (no beforeinstallprompt support)
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isStandalone) return;

    if (isIOSDevice) {
      setIsIOS(true);
      // Delay so the user has time to interact with the page first
      const t = setTimeout(() => setShow(true), 8000);
      return () => clearTimeout(t);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      setShow(false);
    }
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm bg-white border border-stone-200 rounded-2xl shadow-2xl p-4 z-50 animate-fade-in"
      data-testid="install-prompt"
    >
      <button onClick={dismiss} className="absolute top-2 right-2 text-stone-400 hover:text-stone-700 p-1" data-testid="install-prompt-close" aria-label="Dismiss">
        <X size={16} />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#2F4F4F" }}>
          {isIOS ? <AppleLogo size={20} weight="fill" className="text-white" /> : <DownloadSimple size={20} weight="duotone" className="text-amber-300" />}
        </div>
        <div className="flex-1 pr-4">
          <h4 className="text-sm font-semibold text-stone-900">Install Dining Floor Studio</h4>
          {isIOS ? (
            <p className="text-xs text-stone-600 mt-0.5 leading-relaxed">
              Tap the <span className="inline-block px-1.5 py-0.5 bg-stone-100 rounded text-[10px] font-mono">Share</span> icon, then <strong>Add to Home Screen</strong>.
            </p>
          ) : (
            <p className="text-xs text-stone-600 mt-0.5">Get the full-screen app on your home screen — faster load, full-screen, no browser bar.</p>
          )}
          {!isIOS && (
            <Button onClick={install} size="sm" className="mt-3 rounded-full text-xs h-8" style={{ backgroundColor: "#2F4F4F" }} data-testid="install-prompt-cta">
              <DownloadSimple size={12} className="mr-1.5" weight="bold" /> Install app
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
